import { useCallback, useMemo } from 'react';
import { useCities } from 'src/hooks/api/useCities';
import { useKnownCountries } from 'src/hooks/api/useKnownCountries';
import { useStateProvinces } from 'src/hooks/api/useStateProvinces';
import { City } from 'src/WebApiController';

export type UseAddressSelectStateParams = {
  country: string | undefined;
  stateName: string | undefined;
  cityName: string | undefined;
  onCountryChange: (country: string | undefined) => void;
  onStateChange: (stateName: string | undefined) => void;
  onCityChange: (cityName: string | undefined) => void;
};

export type UseAddressSelectStateResult = {
  countryOptions: Record<string, string>;
  stateOptions: Record<string, string>;
  cityOptions: Record<string, string>;
  stateProvinceId: number | undefined;
  cityId: number | undefined;
  countriesLoading: boolean;
  statesLoading: boolean;
  citiesLoading: boolean;
  handleCountryChange: (value: string) => void;
  handleStateChange: (stateId: string) => void;
  handleCityChange: (cityId: string) => void;
};

export function useAddressSelectState({
  country,
  stateName,
  cityName,
  onCountryChange,
  onStateChange,
  onCityChange,
}: UseAddressSelectStateParams): UseAddressSelectStateResult {
  const knownCountriesQuery = useKnownCountries();
  const stateProvincesQuery = useStateProvinces();
  const { citiesWithStateProvinceQuery, citiesWithoutStateProvinceQuery } =
    useCities();

  const stateProvinceId = useMemo(() => {
    if (!stateProvincesQuery.data || !stateName) return undefined;
    const provinces = country
      ? (stateProvincesQuery.data[country] ?? [])
      : Object.values(stateProvincesQuery.data).flat();
    return provinces.find((sp) => sp.name === stateName)?.id;
  }, [stateProvincesQuery.data, stateName, country]);

  const cityId = useMemo(() => {
    if (
      citiesWithStateProvinceQuery.data == null ||
      citiesWithoutStateProvinceQuery.data == null ||
      !cityName
    )
      return undefined;

    const allCities: City[] = [
      ...(country
        ? (citiesWithoutStateProvinceQuery.data[country] ?? [])
        : Object.values(citiesWithoutStateProvinceQuery.data).flat()),
      ...(stateProvinceId != null
        ? (citiesWithStateProvinceQuery.data[stateProvinceId] ?? [])
        : country
          ? (stateProvincesQuery.data?.[country]?.flatMap(
              (sp) => citiesWithStateProvinceQuery.data![sp.id] ?? []
            ) ?? [])
          : Object.values(citiesWithStateProvinceQuery.data).flat()),
    ];
    return allCities.find((c) => c.name === cityName)?.id;
  }, [
    citiesWithStateProvinceQuery.data,
    citiesWithoutStateProvinceQuery.data,
    stateProvincesQuery.data,
    cityName,
    country,
    stateProvinceId,
  ]);

  // Country options: { [countryCode]: countryName }
  const countryOptions = useMemo<Record<string, string>>(() => {
    if (knownCountriesQuery.data == null) return {};
    return Object.entries(knownCountriesQuery.data).reduce(
      (r, [code, name]) => {
        r[code] = name;
        return r;
      },
      {} as Record<string, string>
    );
  }, [knownCountriesQuery.data]);

  // State options: { [id.toString()]: stateName } filtered by country
  const stateOptions = useMemo<Record<string, string>>(() => {
    if (stateProvincesQuery.data == null) return {};
    const provinces = country
      ? (stateProvincesQuery.data[country] ?? [])
      : Object.values(stateProvincesQuery.data).flat();
    return provinces.reduce(
      (r, sp) => {
        if (sp.name) r[sp.id.toString()] = sp.name;
        return r;
      },
      {} as Record<string, string>
    );
  }, [stateProvincesQuery.data, country]);

  // City options: { [id.toString()]: cityName } filtered by country/stateProvinceId
  const cityOptions = useMemo<Record<string, string>>(() => {
    if (
      citiesWithStateProvinceQuery.data == null ||
      citiesWithoutStateProvinceQuery.data == null
    ) {
      return {};
    }

    const cities: City[] = [];

    if (country) {
      cities.push(...(citiesWithoutStateProvinceQuery.data[country] ?? []));
    } else {
      cities.push(
        ...Object.values(citiesWithoutStateProvinceQuery.data).flat()
      );
    }

    if (stateProvinceId != null) {
      cities.push(
        ...(citiesWithStateProvinceQuery.data[stateProvinceId] ?? [])
      );
    } else if (country) {
      const stateIds =
        stateProvincesQuery.data?.[country]?.map((sp) => sp.id) ?? [];
      stateIds.forEach((id) => {
        cities.push(...(citiesWithStateProvinceQuery.data![id] ?? []));
      });
    } else {
      cities.push(...Object.values(citiesWithStateProvinceQuery.data).flat());
    }

    return cities.reduce(
      (r, city) => {
        r[city.id.toString()] = city.name;
        return r;
      },
      {} as Record<string, string>
    );
  }, [
    citiesWithStateProvinceQuery.data,
    citiesWithoutStateProvinceQuery.data,
    stateProvincesQuery.data,
    country,
    stateProvinceId,
  ]);

  const handleCountryChange = useCallback(
    (value: string) => {
      onCountryChange(value || undefined);
      onStateChange(undefined);
      onCityChange(undefined);
    },
    [onCountryChange, onStateChange, onCityChange]
  );

  const handleStateChange = useCallback(
    (stateId: string) => {
      if (!stateId) {
        onStateChange(undefined);
        onCityChange(undefined);
        return;
      }
      const province = country
        ? stateProvincesQuery.data?.[country]?.find(
            (sp) => sp.id === Number(stateId)
          )
        : Object.values(stateProvincesQuery.data ?? {})
            .flat()
            .find((sp) => sp.id === Number(stateId));
      if (province) {
        onStateChange(province.name);
        onCityChange(undefined);
      }
    },
    [country, stateProvincesQuery.data, onStateChange, onCityChange]
  );

  const handleCityChange = useCallback(
    (cityId: string) => {
      if (!cityId) {
        onCityChange(undefined);
        return;
      }
      const allCities: City[] = [
        ...(country
          ? (citiesWithoutStateProvinceQuery.data?.[country] ?? [])
          : Object.values(citiesWithoutStateProvinceQuery.data ?? {}).flat()),
        ...(stateProvinceId != null
          ? (citiesWithStateProvinceQuery.data?.[stateProvinceId] ?? [])
          : country
            ? (stateProvincesQuery.data?.[country]?.flatMap(
                (sp) => citiesWithStateProvinceQuery.data?.[sp.id] ?? []
              ) ?? [])
            : Object.values(citiesWithStateProvinceQuery.data ?? {}).flat()),
      ];
      const city = allCities.find((c) => c.id === Number(cityId));
      if (city) {
        onCityChange(city.name);
      }
    },
    [
      country,
      stateProvinceId,
      citiesWithStateProvinceQuery.data,
      citiesWithoutStateProvinceQuery.data,
      stateProvincesQuery.data,
      onCityChange,
    ]
  );

  return {
    countryOptions,
    stateOptions,
    cityOptions,
    stateProvinceId,
    cityId,
    countriesLoading: knownCountriesQuery.data == null,
    statesLoading: stateProvincesQuery.data == null,
    citiesLoading:
      citiesWithStateProvinceQuery.data == null ||
      citiesWithoutStateProvinceQuery.data == null,
    handleCountryChange,
    handleStateChange,
    handleCityChange,
  };
}
